import { useCallback, useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import { useToast } from "./toast";
import { cleanPhoneMask } from "../Utils/masks";
import {
  PIX_KEYS_LIMIT,
  normalizePixKey,
} from "../Pages/Private/Pix/PixKeys/constants";
import {
  createPixKeyRequest,
  deletePixKeyRequest,
  getPixKeyRequest,
  linkPixKeyRequest,
  listPixKeysRequest,
  unlinkPixKeyRequest,
} from "../Services/pixKeysApi";

const normalizePixKeyValue = (type, value = "") => {
  if (type === "phone") {
    const digits = cleanPhoneMask(value);
    return digits ? `55${digits}` : "";
  }
  if (type === "cpf" || type === "cnpj") {
    return value.replace(/\D/g, "");
  }
  return value.trim();
};

const extractKeys = (payload) => {
  const root = payload?.data ?? payload;

  if (Array.isArray(root)) return root.map(normalizePixKey);
  if (Array.isArray(root?.items)) return root.items.map(normalizePixKey);
  if (Array.isArray(root?.data)) return root.data.map(normalizePixKey);
  if (Array.isArray(root?.keys)) return root.keys.map(normalizePixKey);

  return [];
};

const extractSingleKey = (payload) => {
  const root = payload?.data ?? payload;
  const item = root?.data ?? root?.item ?? root?.key ?? root;
  return normalizePixKey(Array.isArray(item) ? item[0] : item);
};

const getApiError = (error, fallback) => {
  const data = error?.response?.data;

  return {
    title: data?.information || data?.title || data?.detail || data?.error || fallback.title,
    description: data?.description || data?.message || data?.detail || fallback.description,
  };
};

const buildCreatePayload = (type, normalizedValue) => {
  if (type === "evp") {
    return { type: "evp" };
  }

  return {
    type,
    value: normalizedValue,
  };
};

const localeOr = (key, fallback) => {
  const value = intl.get(key);
  return value && value !== key ? value : fallback;
};

export const usePixKeys = () => {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listPixKeysRequest();
      setKeys(extractKeys(response));
    } catch (loadError) {
      setKeys([]);
      const { title, description } = getApiError(loadError, {
        title: "Erro ao carregar chaves",
        description: "Não foi possível listar as chaves Pix da conta.",
      });
      setError({ title, description });
      toastRef.current.addToast({ type: "error", title, description });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const getKey = useCallback(
    async (keyId) => {
      if (!keyId) return null;

      try {
        const response = await getPixKeyRequest(keyId);
        return extractSingleKey(response);
      } catch (error) {
        const { title, description } = getApiError(error, {
          title: "Erro ao consultar chave",
          description: "Não foi possível obter os dados da chave Pix.",
        });
        toast.addToast({ type: "error", title, description });
        return null;
      }
    },
    [toast]
  );

  const createKey = useCallback(
    async ({ type, value }) => {
      if (keys.length >= PIX_KEYS_LIMIT) {
        toast.addToast({
          type: "error",
          title: "Limite atingido",
          description: `Você já possui ${PIX_KEYS_LIMIT} chaves cadastradas.`,
        });
        return false;
      }

      const normalizedValue =
        type === "evp" ? "" : normalizePixKeyValue(type, value);

      if (type !== "evp" && !normalizedValue) {
        toast.addToast({
          type: "error",
          title: "Chave inválida",
          description: "Informe um valor válido para cadastrar a chave Pix.",
        });
        return false;
      }

      const payload = buildCreatePayload(type, normalizedValue);

      setSubmitting(true);
      try {
        const response = await createPixKeyRequest(payload);
        const created = extractSingleKey(response);

        if (!created.id) {
          throw new Error("Resposta inválida ao cadastrar chave Pix.");
        }

        setKeys((prev) => {
          const exists = prev.some((item) => item.id === created.id);
          return exists
            ? prev.map((item) => (item.id === created.id ? created : item))
            : [...prev, created];
        });

        toast.addToast({
          type: "success",
          title: "Chave cadastrada",
          description: "Sua chave Pix foi registrada com sucesso.",
        });
        return true;
      } catch (error) {
        const { title, description } = getApiError(error, {
          title: "Erro ao cadastrar chave",
          description: "Não foi possível registrar a chave Pix.",
        });
        toast.addToast({ type: "error", title, description });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [keys.length, toast]
  );

  const deleteKey = useCallback(
    async (keyId) => {
      if (!keyId) return false;

      setSubmitting(true);
      try {
        await deletePixKeyRequest(keyId);
        setKeys((prev) => prev.filter((item) => item.id !== keyId));
        toast.addToast({
          type: "success",
          title: "Chave excluída",
          description: "A chave Pix foi removida da sua conta.",
        });
        return true;
      } catch (error) {
        const { title, description } = getApiError(error, {
          title: "Erro ao excluir chave",
          description: "Não foi possível remover a chave Pix.",
        });
        toast.addToast({ type: "error", title, description });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [toast]
  );

  const linkKey = useCallback(
    async (keyId, linkedAccountId, originAccountId = null) => {
      if (!keyId || !linkedAccountId) return false;

      setSubmitting(true);
      try {
        await linkPixKeyRequest(keyId, linkedAccountId, originAccountId);
        toast.addToast({
          type: "success",
          title: localeOr("pix.keys.linkSuccess", "Chave vinculada"),
          description: localeOr(
            "pix.keys.linkSuccessHint",
            "A chave foi vinculada à conta virtual. Créditos Pix passam a ir para o destino.",
          ),
        });
        await loadKeys();
        return true;
      } catch (error) {
        const { title, description } = getApiError(error, {
          title: localeOr("pix.keys.linkError", "Erro ao vincular chave"),
          description: localeOr(
            "pix.keys.linkErrorHint",
            "Não foi possível vincular a chave à conta virtual.",
          ),
        });
        toast.addToast({ type: "error", title, description });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [loadKeys, toast]
  );

  const unlinkKey = useCallback(
    async (keyId, originAccountId = null) => {
      if (!keyId) return false;

      setSubmitting(true);
      try {
        await unlinkPixKeyRequest(keyId, originAccountId);
        toast.addToast({
          type: "success",
          title: localeOr("pix.keys.unlinkSuccess", "Chave desvinculada"),
          description: localeOr(
            "pix.keys.unlinkSuccessHint",
            "O vínculo com a conta virtual foi removido.",
          ),
        });
        await loadKeys();
        return true;
      } catch (error) {
        const { title, description } = getApiError(error, {
          title: localeOr("pix.keys.unlinkError", "Erro ao desvincular chave"),
          description: localeOr(
            "pix.keys.unlinkErrorHint",
            "Não foi possível remover o vínculo da chave.",
          ),
        });
        toast.addToast({ type: "error", title, description });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [loadKeys, toast]
  );

  return {
    keys,
    loading,
    error,
    submitting,
    canCreate: keys.length < PIX_KEYS_LIMIT,
    reload: loadKeys,
    getKey,
    createKey,
    deleteKey,
    linkKey,
    unlinkKey,
  };
};
